import React, { useState, useEffect } from 'react';
import { useSearchHighlight } from '../hooks/useSearchHighlight';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import dsgvoData from '../data/pages/dsgvo.json';
import * as S from '../styles/pages/BasePage.styles';

// Spam-Schutz: E-Mail mit Click-to-Reveal & Copy
const ProtectedEmail: React.FC<{ colors: any; language: string; onReveal: () => void }> = ({ colors, language, onReveal }) => {
  const [revealed, setRevealed] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const email = 'hello@kamana.at';

  const revealEmail = () => {
    navigator.clipboard.writeText(email);
    setRevealed(true);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
    onReveal();
  };

  return (
    <>
      {revealed ? (
        <>
          {' '}
          <S.RevealedContent colors={colors}>
            {email}
          </S.RevealedContent>
          {copySuccess && (
            <S.SuccessMessage colors={colors} show={copySuccess}>
              {dsgvoData[language as 'de' | 'en'].success.copied}
            </S.SuccessMessage>
          )}
        </>
      ) : (
        <>
          <S.RevealButton colors={colors} onClick={revealEmail}>
            {dsgvoData[language as 'de' | 'en'].revealButton.show}
          </S.RevealButton>
          {copySuccess && (
            <S.SuccessMessage colors={colors} show={copySuccess}>
              {dsgvoData[language as 'de' | 'en'].success.copied}
            </S.SuccessMessage>
          )}
        </>
      )}
    </>
  );
};

const DSGVO: React.FC = () => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  useSearchHighlight();
  
  // Read language from localStorage (managed by Layout)
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Listen for language changes from Layout
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);
  
  const data = dsgvoData[language];

  return (
    <S.PageWrapper colors={colors}>
      <S.Section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <S.Container>
          <S.PageTitle colors={colors}>
            {data.title}
          </S.PageTitle>
          
          <S.Intro colors={colors} data-text-element="true">
            {data.intro}
          </S.Intro>
          
          {data.sections.map((section: any, index: number) => (
            <div key={section.id}>
              {section.title && (
                <S.SectionTitle 
                  colors={colors}
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {section.title}
                </S.SectionTitle>
              )}
              <S.ContentBlock
                colors={colors}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                data-text-element="true"
              >
                {section.content}
                {section.emailPlaceholder && (
                  <ProtectedEmail 
                    colors={colors} 
                    language={language}
                    onReveal={() => {}}
                  />
                )}
                {section.afterEmail && ' ' + section.afterEmail}
                {/* Render link after content only if content5 doesn't exist */}
                {section.linkText && section.linkUrl && !section.content5 && (
                  <>
                    {' '}
                    <S.StyledLink 
                      colors={colors} 
                      href={section.linkUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {section.linkText}
                    </S.StyledLink>
                  </>
                )}
                {section.afterLink && !section.content5 && ' ' + section.afterLink}
                {section.content2 && (
                  <>
                    {'\n\n'}
                    {section.content2}
                  </>
                )}
                {section.content3 && (
                  <>
                    {'\n\n'}
                    {section.content3}
                  </>
                )}
                {section.content4 && (
                  <>
                    {'\n\n'}
                    {section.content4}
                  </>
                )}
                {section.content5 && (
                  <>
                    {'\n\n'}
                    {section.content5}
                    {section.linkText && section.linkUrl && (
                      <>
                        {' '}
                        <S.StyledLink 
                          colors={colors} 
                          href={section.linkUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {section.linkText}
                        </S.StyledLink>
                      </>
                    )}
                    {section.afterLink && section.afterLink}
                  </>
                )}
              </S.ContentBlock>
            </div>
          ))}
        </S.Container>
      </S.Section>
    </S.PageWrapper>
  );
};

export default DSGVO;

